import { faAsterisk, faMinus, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useResizeObserver from '@react-hook/resize-observer';
import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Tooltip as ChartJSTooltip,
    Filler,
    Legend,
    LineElement,
    LinearScale,
    PointElement,
    TimeScale,
    Title,
} from 'chart.js';
import 'chartjs-adapter-moment';
import queryString from 'query-string';
import { useEffect, useRef, useState } from 'react';
import { Card, Col, Container, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { Line } from 'react-chartjs-2';
import { useLocation, useNavigate } from 'react-router-dom';
import { METRICS_FORECAST_TRENDS, METRICS_FOR_BOARDING_REPORT, METRICS_TRENDS, callQuery } from '../../amplify/api-gateway/query';
import { isDemoUser } from '../../utils/demo';
import { addWeeks, getSqlDateStrFromDateStr } from '../../utils/time';
import { DateType } from '../filters/dateTypes';
import { METRICS_DIMENSION_OPTIONS, METRICS_PERIOD_OPTIONS, METRIC_NAME_TO_DESCRIPTION, METRIC_NAME_TO_DISPLAY, MetricName, isMetricCount } from '../filters/metricsFilterOptions';
import ConfirmationModal from './ConfirmationModal';
import { EvaluationReportFilterBar } from './EvaluationReportFilterBar';
import './Metrics.css';
import { getMappedDimensionValue, getMetricsLineOpts, metricColors } from './metricsPlotOpts';
import { isStacked, removeForecastHorizonFromMetricName } from './metricUtils';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    TimeScale,
    Filler,
    Title,
    ChartJSTooltip,
    Legend,
    PointElement,
    LineElement,
);

/**
 * Metrics dashboard that displays evaluation for the models
 */
export const EvaluationReport = (props: {
    user: any,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryModel = queryString.parse(location.search);

    const [filterDateOptions, setFilterDateOptions]: any = useState<any>({
        dateOptionType: DateType.Relative,
        relativeStart: 7,
        relativeStartScale: "d",
        relativeEnd: 0,
        relativeEndScale: "d",
        absoluteStart: queryModel["start"] ? new Date(Number(queryModel["start"])) : new Date(new Date().getTime() - 1000 * 60 * 60 * 24 * 7),
        absoluteEnd: queryModel["end"] ? new Date(Number(queryModel["end"])) : new Date(),
    })

    const [filterOptions, setFilterOptions]: any = useState<any>({
        "dimension": queryModel["dimension"] ? queryModel["dimension"] : 0,
        "dimension_filter_index": queryModel["dimension_filter_index"] ? queryModel["dimension_filter_index"] : 0,
        "dimension_filter_values": queryModel["dimension_filter_values"] ? queryModel["dimension_filter_values"] : [],
        "period": queryModel["period"] ? queryModel["period"] : 1,
        "display_options": queryModel["display_options"] ? queryModel["display_options"] : ["SHOW_FORECAST", "SHOW_FORECAST_BOUNDS", "SHOW_LAST_WEEK"],
    });

    const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
    const [confirmationTime, setConfirmationTime] = useState<Date>(new Date());

    const [isLoadingMetrics, setIsLoadingMetrics] = useState<boolean>(false);
    const [dimensionValues, setDimensionValues] = useState<string[]>([]);
    const [metricNames, setMetricNames] = useState<string[]>([]);
    const [metricNameToData, setMetricNameToData] = useState<any>({});
    const [metricNameToOps, setMetricNameToOps] = useState<any>({});

    const chartRef = useRef<any>(null);
    const [hoverPosition, setHoverPosition] = useState<Date>();

    const [plotWidth, setPlotWidth] = useState(1024);
    const plotWidthRef = useRef(plotWidth);
    const target = useRef(null);

    useEffect(() => {
        // For optimal performance, always default to 1 day granularity if querying more 
        // than one month of data (since you wouldn't be able to visualize it anyways)
        if ((filterDateOptions.absoluteEnd.getTime() - filterDateOptions.absoluteStart.getTime()) / 1000 >= (60 * 60 * 24 * 31)) {
            setFilterOptions({
                ...filterOptions,
                "period": 2, // index for 1 day granularity
            })
        }
    }, [filterOptions, filterDateOptions])

    useEffect(() => {
        refresh();
    }, []);

    useEffect(() => {
        plotWidthRef.current = plotWidth;
    }, [plotWidth]);

    useEffect(() => {
        if (metricNameToOps && Object.keys(metricNameToOps).length > 0) {
            let localMetricNameToOps: any = {};
            for (let name of Object.keys(metricNameToOps)) {
                localMetricNameToOps[name] = {
                    ...metricNameToOps[name],
                    plugins: {
                        ...metricNameToOps[name].plugins,
                        annotation: {
                            annotations: {
                                line1: {
                                    type: 'line',
                                    xMin: hoverPosition,
                                    xMax: hoverPosition,
                                    borderColor: '#999',
                                    borderDash: [2, 2],
                                    borderWidth: 1,
                                },
                            }
                        },
                    }
                };
            }
            setMetricNameToOps(localMetricNameToOps);
        }
    }, [hoverPosition]);

    const processMetricsData = (updatedMetricNames: string[], metrics: any, forecast: any, lastWeek: any) => {

        if (!metrics || Object.keys(metrics).length === 0) {
            setMetricNameToData({});
            setMetricNameToOps({});
            return;
        }

        let data: any = {};
        let opts: any = {};
        let uniqueDimensionValues = new Set<string>();
        for (let metric of metrics) {
            uniqueDimensionValues.add(getMappedDimensionValue(filterOptions["dimension"], metric[1]));
        }
        let dimensionValues = Array.from(uniqueDimensionValues);
        let dimensionValueToIdx: any = {};
        dimensionValues.forEach((val: string, idx: number) => {
            // dimensionValueToIdx[val] = idx + 1; // To account for the offset of the time field for uPlot only
            dimensionValueToIdx[val] = idx; // To account for the offset of the time field
        })
        setDimensionValues(dimensionValues);

        let preData: any = {};
        let preDataForecast: any = {};
        let preDataLastWeek: any = {};
        let uniqueTimestamps: any = new Set();
        let uniqueTimestampsForecast: any = new Set();
        let uniqueTimestampsLastWeek: any = new Set();
        for (let metricName of updatedMetricNames) {
            preData[metricName] = {};
            preDataForecast[metricName] = {};
            preDataLastWeek[metricName] = {};
            for (let val of dimensionValues) {
                preData[metricName][val] = {};
                preDataForecast[metricName][val] = {};
                preDataLastWeek[metricName][val] = {};
            }
        }
        for (let metric of metrics) {
            const metricName = metric[0];
            const dimensionName = getMappedDimensionValue(filterOptions["dimension"], metric[1]);
            if (isMetricCount(metricName)) {
                // These are counts so we don't do any unit processing for the y-axis
                preData[metricName][dimensionName][metric[2]] = metric[3];
            } else {
                // Display in hours
                preData[metricName][dimensionName][metric[2]] = parseFloat(metric[3]) / 3600;
            }
            uniqueTimestamps.add(metric[2]);
        }

        if (filterOptions["display_options"].includes("SHOW_LAST_WEEK")) {
            for (let metric of lastWeek) {
                const metricName = metric[0];
                // We have queried for the "last week" measurements, so we now need to add back the offset
                const metricTime = addWeeks(new Date(metric[2]), 1).toISOString();
                const dimensionName = getMappedDimensionValue(filterOptions["dimension"], metric[1]);
                if (isMetricCount(metricName)) {
                    // These are counts so we don't do any unit processing for the y-axis
                    preDataLastWeek[metricName][dimensionName][metricTime] = metric[3];
                } else {
                    // Display in hours
                    preDataLastWeek[metricName][dimensionName][metricTime] = parseFloat(metric[3]) / 3600;
                }
                uniqueTimestampsLastWeek.add(metricTime);
            }
        }

        if (Object.keys(forecast).length > 0) {
            for (let metric of forecast) {
                const metricName = removeForecastHorizonFromMetricName(filterOptions, metric[0]);
                const metricDimVal = metric[1];
                const metricTime = metric[2];
                const metricPred = metric[3];
                const metricLower = metric[4];
                const metricUpper = metric[5];
                const dimensionName = getMappedDimensionValue(filterOptions["dimension"], metricDimVal);
                uniqueTimestampsForecast.add(metricTime);
                
                if (isMetricCount(metricName)) {
                    // These are counts so we don't do any unit processes
                    preDataForecast[metricName][dimensionName][metricTime] = {
                        "pred": metricPred,
                        "lower": metricLower,
                        "upper": metricUpper,
                    };
                } else {
                    // Display in hours
                    preDataForecast[metricName][dimensionName][metricTime] = {
                        "pred": parseFloat(metricPred) / 3600,
                        "lower": parseFloat(metricLower) / 3600,
                        "upper": parseFloat(metricUpper) / 3600,
                    };
                }
            }
        }

        const sortedUniqueTimestamps: any = Array.from(uniqueTimestamps).sort();
        const sortedUniqueTimestampsForecast: any = Array.from(uniqueTimestampsForecast).sort();
        const sortedUniqueTimestampsLastWeek: any = Array.from(uniqueTimestampsLastWeek).sort();
        const maxTimestamp = sortedUniqueTimestamps.length > 0 ?sortedUniqueTimestamps[sortedUniqueTimestamps.length - 1] : null;

        //
        // Format for Chart.js
        //
        for (let metricName of updatedMetricNames) {
            data[metricName] = {
                datasets: []
            };
            dimensionValues.forEach((val: string, idx: number) => {
                data[metricName].datasets.push({
                    data: [],
                    label: val,
                    borderColor: metricColors(METRICS_DIMENSION_OPTIONS[filterOptions["dimension"]].name, idx, val),
                    backgroundColor: metricColors(METRICS_DIMENSION_OPTIONS[filterOptions["dimension"]].name, idx, val),
                    hoverBorderColor: "blue",
                    hoverBackgroundColor: "blue",
                    fill: isStacked(filterOptions, metricName),
                    // spanGaps: 1000 * 60 * 60 * 1, // Setting this will cause the shaded region to disappear at day granularity
                    borderWidth: 1,
                    hoverBorderWidth: 2,
                    pointRadius: 1,
                    pointHoverRadius: 2,
                });
            });

            // Add the forecast bounds (if applicable)
            if (metricName === MetricName.ActivePatients) {
                const forecastedLower: any = [];
                const forecastedUpper: any = [];
                const forecastedPred: any = [];
                for (let ts of sortedUniqueTimestampsForecast) {
                    for (let metricName of updatedMetricNames) {
                        for (let val of dimensionValues) {
                            if (preDataForecast[metricName] && preDataForecast[metricName][val] && preDataForecast[metricName][val][ts]) {
                                if ((filterOptions["display_options"].includes("SHOW_PAST_FORECAST") && ts < maxTimestamp) 
                                    || (filterOptions["display_options"].includes("SHOW_FORECAST") && ts >= maxTimestamp)) {
                                    forecastedPred.push({
                                        x: new Date(ts),
                                        y: preDataForecast[metricName][val][ts]["pred"]
                                    })
                                }
                                if ((filterOptions["display_options"].includes("SHOW_PAST_FORECAST_BOUNDS") && ts < maxTimestamp) 
                                    || (filterOptions["display_options"].includes("SHOW_FORECAST_BOUNDS") && ts >= maxTimestamp)) {
                                    forecastedLower.push({
                                        x: new Date(ts),
                                        y: preDataForecast[metricName][val][ts]["lower"]
                                    })
                                    forecastedUpper.push({
                                        x: new Date(ts),
                                        y: preDataForecast[metricName][val][ts]["upper"]
                                    })
                                }
                            }
                        }
                    }
                }

                if (filterOptions["display_options"].includes("SHOW_FORECAST_BOUNDS") || filterOptions["display_options"].includes("SHOW_PAST_FORECAST_BOUNDS")) {
                    data[metricName].datasets.push({
                        data: forecastedLower,
                        label: "lower",
                        // borderColor: "rgba(202, 231, 252, 0.6)", // blue
                        borderColor: "rgba(232, 135, 56, 1)", // orange
                        // backgroundColor: createDiagonalPattern("#7f7f7f"),
                        // backgroundColor: "rgba(202, 231, 252, 0.3)", // blue
                        backgroundColor: "rgba(232, 135, 56, 0.3)", // orange
                        fill: '+1',
                        // borderDash: [2, 2],
                        borderWidth: 0,
                        hoverBorderWidth: 1,
                        pointRadius: 0,
                        pointHoverRadius: 2,
                    });

                    data[metricName].datasets.push({
                        data: forecastedUpper,
                        label: "upper",
                        // borderColor: "rgba(202, 231, 252, 0.6)", // blue
                        borderColor: "rgba(232, 135, 56, 1)", // orange
                        fill: false,
                        // borderDash: [2, 2],
                        borderWidth: 0,
                        hoverBorderWidth: 1,
                        pointRadius: 0,
                        pointHoverRadius: 2,
                    });
                }

                if (filterOptions["display_options"].includes("SHOW_FORECAST") || filterOptions["display_options"].includes("SHOW_PAST_FORECAST")) {
                    data[metricName].datasets.push({
                        data: forecastedPred,
                        label: "pred",
                        borderColor: "rgba(232, 135, 56, 1)",
                        fill: false,
                        borderDash: [2, 2],
                        borderWidth: 2,
                        hoverBorderWidth: 2,
                        pointRadius: 0,
                        pointHoverRadius: 2,
                    });
                }
            }
            // Add one day to the end to account for forecasting
            opts[metricName] = getMetricsLineOpts(isStacked(filterOptions, metricName), filterDateOptions.absoluteStart, new Date(filterDateOptions.absoluteEnd.getTime() + (1000 * 60 * 60 * 24)), chartRef, hoverPosition, setHoverPosition, setConfirmationTime, setShowConfirmationModal);
        }

        //
        // Add the data for the main plot
        //
        for (let ts of sortedUniqueTimestamps) {
            for (let metricName of updatedMetricNames) {
                for (let val of dimensionValues) {
                    if (preData[metricName][val][ts]) {
                        data[metricName].datasets[dimensionValueToIdx[val]].data.push({
                            x: new Date(ts),
                            y: preData[metricName][val][ts]
                        })
                    }
                }
            }
        }

        //
        // Add the last week line when appropriate
        //
        if (sortedUniqueTimestampsLastWeek.length > 0) {
            for (let metricName of updatedMetricNames) {
                let lastWeekDimensionValueToIdx: any = {};
                dimensionValues.forEach((val: string, idx: number) => {
                    lastWeekDimensionValueToIdx[val] = data[metricName].datasets.length;
                    data[metricName].datasets.push({
                        data: [],
                        label: val + " (last week)",
                        borderColor: "#9e9e9e",
                        backgroundColor: "#9e9e9e",
                        // hoverBorderColor: "blue",
                        // hoverBackgroundColor: "blue",
                        borderDash: [2, 2],
                        // fill: isStacked(metricName),
                        // spanGaps: 1000 * 60 * 60 * 1, // Setting this will cause the shaded region to disappear at day granularity
                        borderWidth: 1,
                        hoverBorderWidth: 2,
                        pointRadius: 0,
                        pointHoverRadius: 0,
                    });
                });

                for (let ts of sortedUniqueTimestampsLastWeek) {
                    for (let val of dimensionValues) {
                        if (preDataLastWeek[metricName][val][ts]) {
                            data[metricName].datasets[lastWeekDimensionValueToIdx[val]].data.push({
                                x: new Date(ts),
                                y: preDataLastWeek[metricName][val][ts]
                            })
                        }
                    }
                }
            }
        }

        // console.log("Setting new data to be", data)
        setMetricNameToData(data);
        setMetricNameToOps(opts);
    }

    const refresh = () => {
        let updatedMetricNames = [MetricName.ActualAdmitRiskAUROC, MetricName.RevisitScoreAUROC, MetricName.ActualAdmitRiskTP, MetricName.ActualAdmitRiskFP, MetricName.ActiveBoardingMAE, MetricName.ActiveBedRequestsMAE, MetricName.ActiveRoomedNotDispoMAE, MetricName.ActualForecastPatientsMAE];

        setMetricNames(updatedMetricNames);

        setIsLoadingMetrics(true);
        const promises: any = [];
        const promiseOperations: string[] = []; // Keeps track of what each promise represents
        promises.push(
            callQuery({
                operation: METRICS_TRENDS,
                period: METRICS_PERIOD_OPTIONS[filterOptions["period"]].id,
                start: getSqlDateStrFromDateStr(filterDateOptions.absoluteStart),
                end: getSqlDateStrFromDateStr(filterDateOptions.absoluteEnd),
                dimension_name: METRICS_DIMENSION_OPTIONS[filterOptions["dimension"]].id,
                metric_names: updatedMetricNames,
                filter_dimension_name: METRICS_DIMENSION_OPTIONS[filterOptions["dimension_filter_index"]].id,
                filter_dimension_values: filterOptions["dimension_filter_values"],
            })
        )
        promiseOperations.push(METRICS_TRENDS);

        // TODO: Get forecasts of the other plots
        promises.push(
            callQuery({
                operation: METRICS_FORECAST_TRENDS,
                period: METRICS_PERIOD_OPTIONS[filterOptions["period"]].id,
                start: getSqlDateStrFromDateStr(filterDateOptions.absoluteStart),
                end: getSqlDateStrFromDateStr(new Date(filterDateOptions.absoluteEnd.getTime() + (1000 * 60 * 60 * 24))),
                dimension_name: "ALL",
                metric_names: updatedMetricNames,
                filter_dimension_name: "ALL",
                filter_dimension_values: [],
            })
        )
        promiseOperations.push(METRICS_FORECAST_TRENDS);

        //
        // Run query to get the "last week" line 
        // Only applies when we see one line on the UI as it will get confusing
        //
        promises.push(
            callQuery({
                operation: METRICS_TRENDS,
                period: METRICS_PERIOD_OPTIONS[filterOptions["period"]].id,
                start: getSqlDateStrFromDateStr(addWeeks(filterDateOptions.absoluteStart, -1)),
                end: getSqlDateStrFromDateStr(addWeeks(filterDateOptions.absoluteEnd, -1)),
                dimension_name: METRICS_DIMENSION_OPTIONS[filterOptions["dimension"]].id,
                metric_names: updatedMetricNames,
                filter_dimension_name: METRICS_DIMENSION_OPTIONS[filterOptions["dimension_filter_index"]].id,
                filter_dimension_values: filterOptions["dimension_filter_values"],
            })
        )
        promiseOperations.push(METRICS_TRENDS + "_LAST_WEEK");

        Promise.all(promises).then((promiseOutput: any) => {
            let metricsTrendOutput: any = null;
            let metricsPredictionOutput: any = [];
            let metricsLastWeekOutput: any = [];
            for (let i = 0; i < promises.length; i++) {
                if (promiseOperations[i] === METRICS_TRENDS) {
                    metricsTrendOutput = promiseOutput[i];
                } else if (promiseOperations[i] === METRICS_FORECAST_TRENDS) {
                    metricsPredictionOutput = promiseOutput[i];
                } else if (promiseOperations[i] === METRICS_TRENDS + "_LAST_WEEK") {
                    metricsLastWeekOutput = promiseOutput[i];
                }
            }
            processMetricsData(updatedMetricNames, metricsTrendOutput, metricsPredictionOutput, metricsLastWeekOutput);
        })
        .finally(() => {
            setIsLoadingMetrics(false);
        });
    
    }

    // Run on subsequent iterations page width changes
    //
    useResizeObserver(target, (entry: any) => {
        console.log("useResizeObserver called with ", entry.contentRect.width)
        let resizeTo = entry.contentRect.width - 60;
        triggerResize(resizeTo);
    })

    const triggerResize = (resizeTo: number) => {
        setPlotWidth(resizeTo);
    }

    const updateFilterOptions = (option: string, val: any) => {
        if (option === "dimension") {
            setFilterOptions({
                ...filterOptions,
                "dimension_filter_index": val,
                "dimension_filter_values": [],
                [option]: val,
            });
        } else if (option === "dimension_filter_index") {
            setFilterOptions({
                ...filterOptions,
                "dimension_filter_values": [],
                [option]: val,
            });
        } else {
            setFilterOptions({
                ...filterOptions,
                [option]: val,
            });
        }
    }

    return (
        <div ref={target}>
            <div className="metrics-sidebar" style={{overflowY: 'scroll', marginTop: isDemoUser(props.user) ? '76px' : undefined}}>
                <EvaluationReportFilterBar
                    isLoadingMetrics={isLoadingMetrics}
                    filterOptions={filterOptions}
                    updateFilterOptions={updateFilterOptions}
                    filterDateOptions={filterDateOptions}
                    setFilterDateOptions={setFilterDateOptions}
                    refresh={refresh}
                />
            </div>
            <Container fluid className="metrics-container" style={{marginTop: isDemoUser(props.user) ? '140px' : undefined}}>
                {
                    (!isLoadingMetrics && Object.keys(metricNameToData).length === 0) && (
                        <Row className="mt-2 mb-2">
                            <Col md={12}>
                                <div style={{marginTop: '20%', marginLeft: '20%', marginRight: '20%'}}>
                                    <h3>
                                        <FontAwesomeIcon icon={faAsterisk} />{' '}No Results Found
                                    </h3>
                                    <hr />
                                    <h6 style={{color: '#999999'}}>
                                        No data was found for the current parameters - try adjusting the filters.
                                    </h6>
                                </div>
                            </Col>
                        </Row>
                    )
                }
                {
                    Object.keys(metricNameToData).length > 0 && metricNames.map(metricName => (
                        <Row key={metricName} className="mt-2 mb-2">
                            <Col md={12}>
                                <Card>
                                    <Card.Body>
                                        <div style={{ position: 'absolute', left: '16px', top: '12px', width: '370px', fontSize: '0.9rem'}}>
                                            <strong>{METRIC_NAME_TO_DISPLAY[metricName] ? METRIC_NAME_TO_DISPLAY[metricName] : metricName}</strong>{' '}
                                            <OverlayTrigger
                                                placement="right"
                                                overlay={
                                                    <Tooltip>
                                                        {METRIC_NAME_TO_DESCRIPTION[metricName]}
                                                    </Tooltip>
                                                }
                                            >
                                                <span><FontAwesomeIcon icon={faQuestionCircle} /></span>
                                            </OverlayTrigger>
                                        </div>
                                        <div className="text-right" style={{ position: 'absolute', right: '16px', top: '4px', width: '820px' }}>
                                            <div className="text-right float-end plot-indicator-container">
                                                {/* {
                                                    dimensionValues.map((dimensionValue: string, idx: number) => (
                                                        <div key={dimensionValue} className='plot-indicator-container-item'>
                                                            <span className="plot-indicator-container-icon" style={{ fontSize: '1rem', color: metricColors(METRICS_DIMENSION_OPTIONS[filterOptions["dimension"]].name, idx, dimensionValue) }}><FontAwesomeIcon icon={faCircle} /></span>{' '}<span className="plot-indicator-container-desc">{dimensionValue}</span>
                                                        </div>
                                                    ))
                                                } */}
                                                {
                                                    metricName === MetricName.ActivePatients && (filterOptions["display_options"].includes("SHOW_FORECAST") || filterOptions["display_options"].includes("SHOW_FORECAST_BOUNDS")) && (
                                                        <div className='plot-indicator-container-item'>
                                                            <span className="plot-indicator-container-icon" style={{ fontSize: '1rem', color: 'rgba(232, 135, 56, 1)' }}><FontAwesomeIcon icon={faMinus} /></span>{' '}<span className="plot-indicator-container-desc">Forecasted</span>
                                                        </div>
                                                    )
                                                }
                                                {
                                                    (filterOptions["display_options"].includes("SHOW_LAST_WEEK")) && (
                                                        <div className='plot-indicator-container-item'>
                                                            <span className="plot-indicator-container-icon" style={{ fontSize: '1rem', color: '#9e9e9e' }}><FontAwesomeIcon icon={faMinus} /></span>{' '}<span className="plot-indicator-container-desc">Last Week</span>
                                                        </div>
                                                    )
                                                }
                                            </div>
                                        </div>
                                        <div style={{ marginTop: '24px' }}>
                                            {
                                                metricNameToData && metricNameToData[metricName] && metricNameToData[metricName].datasets.length > 0 && metricNameToOps && metricNameToOps[metricName] && (
                                             
                                                    <div>
                                                        {
                                                            // @ts-ignore
                                                            <Line options={metricNameToOps[metricName]} data={metricNameToData[metricName]} height="240px" ref={chartRef} />
                                                        }
                                                    </div>
                                                )
                                            }
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    ))
                }
            </Container>
            <ConfirmationModal showConfirmationModal={showConfirmationModal} setShowConfirmationModal={setShowConfirmationModal} confirmTime={confirmationTime} />
        </div>
    );
};
